<script setup lang="ts">
import { computed, onMounted, onUnmounted } from "vue";
import Button from "@/Components/Backend/Ui/Button.vue";
import Loader from "@/Components/Backend/Ui/Loader.vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";

// --- Props
const props = withDefaults(
    defineProps<{
        maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "screen";
        closeable?: boolean;
        loading?: boolean;
        title?: string;
        rounded?: boolean;
        titleStyle?: "inline" | "block";
    }>(),
    {
        maxWidth: "2xl",
        closeable: true,
        loading: false,
        title: undefined,
        rounded: true,
        titleStyle: "block",
    },
);
// --- Emit
const emit = defineEmits(["close"]);
// --- Data
// --- Computed
const maxWidthClass = computed<string>(() => {
    return {
        sm: "sm:max-w-sm",
        md: "sm:max-w-md",
        lg: "sm:max-w-lg",
        xl: "sm:max-w-xl",
        "2xl": "sm:max-w-2xl",
        "3xl": "sm:max-w-3xl",
        "4xl": "sm:max-w-4xl",
        "5xl": "sm:max-w-5xl",
        screen: "sm:max-w-screen",
    }[props.maxWidth];
});
const titleClass = computed<string>(() => {
    return {
        inline: "text-center font-poppins text-lg lg:text-2xl py-4 uppercase border-b",
        block: "text-lg font-bold",
    }[props.titleStyle];
});
// --- Methods
const closeOnEscape = (e: KeyboardEvent) => {
    if (e.key === "Escape" && props.closeable) {
        emit("close");
    }
};
const close = () => {
    if (props.closeable) {
        emit("close");
    }
};
// --- Watch
// --- Provide
// --- Mount
onMounted(() => {
    document.addEventListener("keydown", closeOnEscape);
});
onUnmounted(() => {
    document.removeEventListener("keydown", closeOnEscape);
    document.body.style.overflow = "visible";
});
</script>

<template>
    <div
        class="transform overflow-hidden bg-white shadow-xl transition-all sm:mx-auto sm:w-full"
        :class="[maxWidthClass, rounded ? 'rounded-md' : 'rounded-none']"
    >
        <Loader v-if="loading" :text="$t('Loading...')" />

        <div v-show="!loading" class="modal">
            <slot v-if="$slots.title" name="title" />

            <div
                v-else-if="title"
                class="flex items-center py-4 pl-6 pr-12"
                :class="{ 'justify-center': titleStyle === 'inline', 'border-b': titleStyle === 'block' }"
            >
                <h2 :class="titleClass">
                    {{ title }}
                </h2>

                <Button
                    v-if="closeable"
                    type="button"
                    variant="ghost"
                    class="absolute right-2.5 top-2.5 !px-2.5"
                    @click="close"
                >
                    <XMarkIcon class="h-5 w-5" />
                </Button>
            </div>

            <slot />
        </div>
    </div>
</template>

<style lang="postcss" scoped>
.modal {
    overflow-y: auto;
    max-height: calc(100vh - 2rem);
    -webkit-overflow-scrolling: touch;
}
</style>
